import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import {
  prefillFromProfile,
  type ApplicationAnswer,
  type ApplicationFieldKind,
  type ClassifiedField,
  type UserProfile,
} from "@resume-cloudflare/shared";
import {
  ClipboardCheck,
  Copy,
  ExternalLink,
  RefreshCcw,
  Send,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import {
  useApplyWorkshop,
  useExtractApplication,
  useGenerateAnswer,
  usePatchAnswer,
  useSubmitApplication,
} from "../lib/apply";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";
import { Textarea } from "../components/ui/Textarea";

// Two-column workshop. Left = field cards keyed by classifier kind. Right =
// the application URL (in an iframe when X-Frame-Options permit; otherwise a
// deep-link card with "Open in new tab"). Field cards show the classified
// kind + the prefill value for personal/links/demographic, or the generated
// draft for essay/experience.

export default function ApplicationAssistant() {
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId;
  const workshop = useApplyWorkshop(applicationId);
  const extractMutation = useExtractApplication();
  const generate = useGenerateAnswer(applicationId);
  const submit = useSubmitApplication(applicationId);

  const data = workshop.data;
  const application = data?.application;

  const onExtract = () => {
    if (!application) return;
    extractMutation.mutate(
      { application_url: application.url },
      {
        onSuccess: () => {
          // Refetch the workshop bundle so cards land in classifier order
          // backed by the same shape the GET endpoint returns.
          void workshop.refetch();
        },
      },
    );
  };

  if (!applicationId) {
    return (
      <section className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Missing application id"
          description="This page expects /applications/:id/apply."
        />
      </section>
    );
  }

  if (workshop.isLoading) {
    return (
      <section className="mx-auto max-w-7xl space-y-3 p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </section>
    );
  }

  if (workshop.error || !data || !application) {
    return (
      <section className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
        <EmptyState
          tone="danger"
          title="Couldn't load application"
          description="The id may be invalid or you don't have access."
        />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6 lg:p-8">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            Application assistant
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            <Link
              to="/applications"
              className="text-neutral-300 underline-offset-4 hover:underline"
            >
              {application.role}
            </Link>{" "}
            ·{" "}
            <a
              href={application.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-neutral-300 underline-offset-4 hover:underline"
            >
              Open posting <ExternalLink size={11} aria-hidden />
            </a>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data.classified.length > 0 ? (
            <Link to={`/applications/${applicationId}/apply-kit`}>
              <Button variant="ghost" size="sm" leadingIcon={<Copy size={12} aria-hidden />}>
                Copy-paste kit
              </Button>
            </Link>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            loading={extractMutation.isPending}
            onClick={onExtract}
            leadingIcon={<RefreshCcw size={12} aria-hidden />}
          >
            {data.classified.length === 0 ? "Extract form" : "Re-extract"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={data.answers.filter((a) => a.accepted_at == null).length === 0}
            loading={submit.isPending}
            onClick={() => submit.mutate()}
            leadingIcon={<Send size={12} aria-hidden />}
          >
            Mark as submitted
          </Button>
        </div>
      </header>

      {data.extraction?.degraded ? (
        <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-sm text-warning-fg">
          <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            We couldn't read this form reliably (Workday-style virtualized
            forms are common). Open in a new tab and use this page as a
            companion drafter.
          </span>
        </div>
      ) : null}

      {data.classified.length === 0 ? (
        <EmptyState
          icon={<Sparkles size={16} aria-hidden />}
          title="No fields extracted yet"
          description="Click Extract form to load the application URL and pull out its fields."
          action={
            <Button
              variant="primary"
              size="sm"
              loading={extractMutation.isPending}
              onClick={onExtract}
              leadingIcon={<Sparkles size={12} aria-hidden />}
            >
              Extract form
            </Button>
          }
        />
      ) : (
        <FieldsGrid
          classified={data.classified}
          answers={data.answers}
          profile={data.profile}
          onGenerate={(field) =>
            generate.mutate({ application_id: applicationId, field })
          }
          generatePending={generate.isPending}
          generatePendingFor={generate.variables?.field?.name ?? null}
          applicationId={applicationId}
        />
      )}
    </section>
  );
}

// ── Field rendering ─────────────────────────────────────────────────────

function FieldsGrid({
  classified,
  answers,
  profile,
  onGenerate,
  generatePending,
  generatePendingFor,
  applicationId,
}: {
  classified: ClassifiedField[];
  answers: ApplicationAnswer[];
  profile: UserProfile | null;
  onGenerate: (field: ClassifiedField) => void;
  generatePending: boolean;
  generatePendingFor: string | null;
  applicationId: string;
}) {
  const grouped = useMemo(() => {
    const buckets = new Map<ApplicationFieldKind, ClassifiedField[]>();
    for (const f of classified) {
      const arr = buckets.get(f.kind) ?? [];
      arr.push(f);
      buckets.set(f.kind, arr);
    }
    const order: ApplicationFieldKind[] = [
      "personal",
      "links",
      "auth",
      "compensation",
      "demographic",
      "experience",
      "essay",
      "unknown",
    ];
    return order
      .map((kind) => ({ kind, fields: buckets.get(kind) ?? [] }))
      .filter((b) => b.fields.length > 0);
  }, [classified]);

  const answerByName = useMemo(
    () => new Map(answers.map((a) => [a.field_name, a])),
    [answers],
  );

  return (
    <div className="space-y-5">
      {grouped.map(({ kind, fields }) => (
        <Card key={kind}>
          <CardHeader
            title={KIND_LABELS[kind]}
            description={KIND_DESCRIPTIONS[kind]}
          />
          <CardBody className="space-y-3">
            {fields.map((f) => (
              <FieldCard
                key={f.name}
                field={f}
                profile={profile}
                answer={answerByName.get(f.name)}
                onGenerate={() => onGenerate(f)}
                generating={generatePending && generatePendingFor === f.name}
                applicationId={applicationId}
              />
            ))}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

const KIND_LABELS: Record<ApplicationFieldKind, string> = {
  personal: "Personal",
  links: "Links",
  auth: "Work authorization",
  compensation: "Compensation",
  demographic: "Demographics",
  experience: "Experience",
  essay: "Essays",
  unknown: "Other",
};

const KIND_DESCRIPTIONS: Record<ApplicationFieldKind, string> = {
  personal: "Auto-filled from your /profile.",
  links: "Auto-filled from your /profile.",
  auth: "Auto-filled from your /profile (work_authorization, sponsorship).",
  compensation: "Default from /profile; review per application.",
  demographic: "Auto-filled from your /profile demographic answers.",
  experience: "Generated drafts (years, current title, ‘how did you hear’ etc.).",
  essay: "Retrieval-driven drafts in your voice. Edit any answer; submit feeds the artifacts loop.",
  unknown: "Resume uploads, agreement checkboxes, anything we couldn't classify.",
};

function FieldCard({
  field,
  profile,
  answer,
  onGenerate,
  generating,
  applicationId,
}: {
  field: ClassifiedField;
  profile: UserProfile | null;
  answer: ApplicationAnswer | undefined;
  onGenerate: () => void;
  generating: boolean;
  applicationId: string;
}) {
  const prefill =
    field.profile_key && profile
      ? prefillFromProfile(profile, field.profile_key)
      : null;

  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950/50 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-neutral-100">
            {field.label || field.name}
            {field.required ? (
              <span className="ml-1 text-danger-fg" aria-label="required">
                *
              </span>
            ) : null}
          </p>
          <p className="text-[11px] text-neutral-500">
            <code>{field.name}</code> · {field.type}
          </p>
        </div>
        <FieldKindPill kind={field.kind} />
      </div>

      {field.kind === "essay" || field.kind === "experience" ? (
        <EssayEditor
          answer={answer}
          onGenerate={onGenerate}
          generating={generating}
          applicationId={applicationId}
        />
      ) : (
        <PrefillEditor value={prefill} />
      )}
    </div>
  );
}

function FieldKindPill({ kind }: { kind: ApplicationFieldKind }) {
  const tone: Record<ApplicationFieldKind, "accent" | "neutral" | "warning"> = {
    personal: "neutral",
    links: "neutral",
    auth: "neutral",
    compensation: "warning",
    demographic: "neutral",
    experience: "accent",
    essay: "accent",
    unknown: "warning",
  };
  return <Pill tone={tone[kind]}>{kind}</Pill>;
}

function PrefillEditor({ value }: { value: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) {
    return (
      <p className="mt-2 text-xs text-neutral-500">
        No profile value. Fill out{" "}
        <Link to="/profile" className="underline-offset-4 hover:underline">
          /profile
        </Link>{" "}
        to enable prefill for this field.
      </p>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <code className="block min-w-0 flex-1 truncate rounded bg-neutral-900 px-2 py-1 text-xs text-neutral-200">
        {value}
      </code>
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={
          copied ? <ClipboardCheck size={12} aria-hidden /> : <Copy size={12} aria-hidden />
        }
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function EssayEditor({
  answer,
  onGenerate,
  generating,
  applicationId,
}: {
  answer: ApplicationAnswer | undefined;
  onGenerate: () => void;
  generating: boolean;
  applicationId: string;
}) {
  const patch = usePatchAnswer(applicationId);
  const [draft, setDraft] = useState(answer?.answer ?? "");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setDraft(answer?.answer ?? "");
  }, [answer?.id, answer?.answer]);

  const dirty = draft !== (answer?.answer ?? "");

  return (
    <div className="mt-2 space-y-2">
      {answer ? (
        <Textarea
          rows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (answer && dirty) {
              patch.mutate({ id: answer.id, answer: draft });
            }
          }}
        />
      ) : (
        <p className="text-xs text-neutral-500">
          No draft yet. Generate a first pass — uses your career corpus +
          past application artifacts.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-neutral-500">
          {answer?.user_edited ? "Edited · " : null}
          {answer?.accepted_at
            ? `Submitted ${new Date(answer.accepted_at).toLocaleString()}`
            : answer
              ? "Draft"
              : null}
        </div>
        <div className="flex items-center gap-2">
          {answer ? (
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={
                copied ? <ClipboardCheck size={12} aria-hidden /> : <Copy size={12} aria-hidden />
              }
              onClick={async () => {
                await navigator.clipboard.writeText(answer.answer);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : null}
          <Button
            variant={answer ? "ghost" : "primary"}
            size="sm"
            loading={generating}
            onClick={onGenerate}
            leadingIcon={<Sparkles size={12} aria-hidden />}
          >
            {answer ? "Regenerate" : "Draft"}
          </Button>
        </div>
      </div>
      {answer?.sources && answer.sources.length > 0 ? (
        <div className="text-[11px] text-neutral-500">
          Drew from {answer.sources.length} source
          {answer.sources.length === 1 ? "" : "s"}.{" "}
          <Link to="/corpus" className="underline-offset-4 hover:underline">
            Inspect corpus
          </Link>
        </div>
      ) : null}
    </div>
  );
}

